// src/marketing/content/ContentPage.tsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ContentKind, ContentPlatform } from '../../lib/types'
import { useAppStore } from '../../shell/appStore'
import { useContents, usePatchContentStatus } from './useContents'
import { ContentBoard } from './ContentBoard'
import { ContentCalendar } from './ContentCalendar'
import { KIND_LABELS, PLATFORM_LABELS } from './statuses'

const selectStyle: React.CSSProperties = {
  background: 'var(--elevated)',
  border: '1px solid var(--line-strong)',
  borderRadius: '7px',
  padding: '0.4375rem 0.75rem',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
}

export function ContentPage() {
  const navigate = useNavigate()
  // Sélecteur zustand : pattern majoritaire des pages (PipelinePage, CampaignsPage, LaunchPage).
  const appId = useAppStore((s) => s.activeAppId)
  const [view, setView] = useState<'kanban' | 'calendar'>('kanban')
  const [kindFilter, setKindFilter] = useState<ContentKind | ''>('')
  const [platformFilter, setPlatformFilter] = useState<ContentPlatform | ''>('')

  const { data: contents = [], isLoading, isError } = useContents(appId, {
    kind: kindFilter,
    platform: platformFilter,
  })
  const patchStatus = usePatchContentStatus(appId)

  // Garde app active — même early-return que PipelinePage / TasksPage.
  // Tous les hooks sont appelés avant ce return (règle des hooks).
  if (!appId) {
    return (
      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Sélectionnez une app pour voir les contenus.
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem 2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--fg)', margin: 0, letterSpacing: '-0.025em' }}>
          Contenus
        </h1>
        <button
          onClick={() => navigate('/marketing/content/new')}
          style={{
            background: 'var(--arancio)', color: '#fff', border: 'none', borderRadius: '7px',
            padding: '0.4375rem 0.875rem', fontSize: '0.875rem', fontWeight: 600,
            fontFamily: 'inherit', cursor: 'pointer',
          }}
        >
          + Nouveau contenu
        </button>
      </div>

      {/* Bascule de vue + filtres */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {(['kanban', 'calendar'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              background: view === v ? 'var(--elevated)' : 'transparent',
              border: '1px solid',
              borderColor: view === v ? 'var(--line-strong)' : 'var(--line)',
              borderRadius: '7px',
              padding: '0.3125rem 0.75rem',
              color: view === v ? 'var(--fg)' : 'var(--muted)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {v === 'kanban' ? 'Kanban' : 'Calendrier'}
          </button>
        ))}
        <select
          aria-label="Filtrer par type"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as ContentKind | '')}
          style={selectStyle}
        >
          <option value="">Tous les types</option>
          {Object.entries(KIND_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          aria-label="Filtrer par plateforme"
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value as ContentPlatform | '')}
          style={selectStyle}
        >
          <option value="">Toutes les plateformes</option>
          {Object.entries(PLATFORM_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {isLoading && <p style={{ color: 'var(--muted)' }}>Chargement…</p>}
      {isError && <p style={{ color: 'var(--arancio)' }}>Erreur lors du chargement des contenus.</p>}
      {patchStatus.isError && (
        <p style={{ color: 'var(--arancio)', fontSize: '0.8125rem' }}>
          Échec du déplacement — état restauré.
        </p>
      )}

      {!isLoading && !isError && view === 'kanban' && (
        <ContentBoard
          contents={contents}
          onStatusChange={(id, status) => patchStatus.mutate({ id, status })}
          onOpen={(id) => navigate(`/marketing/content/${id}`)}
          isPatchPending={patchStatus.isPending}
        />
      )}
      {!isLoading && !isError && view === 'calendar' && (
        <ContentCalendar
          contents={contents}
          onDayClick={(iso) => navigate(`/marketing/content/new?date=${iso}`)}
          onContentClick={(id) => navigate(`/marketing/content/${id}`)}
        />
      )}
    </div>
  )
}
